<script setup lang="ts">
import { computed } from "vue";
import {
  ButtonSeverity,
  ButtonSize,
  TuButton,
} from "@tinyurldev/tu-ui-vue";

import Favicon from "@Components/Favicon.vue";
import UserAvatar from "@Modules/urls/Components/UserAvatar.vue";
import useTranslation from "@Composables/useTranslation.ts";
import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory.ts";
import { Alias } from "@Modules/urls/Data/Models/AliasFactory";
import { Tag } from "@Modules/urls/Data/Models/TagModel.ts";
import { toRelativeTime } from "@Helpers/support";

//
// Props
interface Props {
  item: UrlAlias,
  selected?: string | null,
  locale?: string,
}

const props = withDefaults(defineProps<Props>(), {
  locale: "en",
  selected: "",
});

//
// Emit
interface Events {
  (e: "tag", name: string): void;
  (e: "url-selected"): void;
}

const emit = defineEmits<Events>();

//
// Setup
const { __ } = useTranslation();

//
// Computed
const alias = computed<Alias | null>(() => props.item?.aliases?.find((a) => a.is_main) ?? null);
//
// Methods
const selectTinyurl = () => {
  emit("url-selected");

  props.item.visit();
};

function onTagClick(tag: Tag): void {
  tag.visit();
  emit("tag", tag.name);
}
</script>

<template>
  <div
    v-if="alias"
    class="url-list-item"
    :class="[
      `_li _li_${alias.alias}`,
      (`${alias.domain}/${alias.alias}` === props.selected) ? 'active' : '',
    ]"
    @click="selectTinyurl"
  >
    <div class="url-list-item__icon-wrapper">
      <Favicon
        class="_row_icon url-list-item__icon"
        :hostname="item.host"
        :size="'26px'"
        :url="item.url"
      />
    </div>

    <div class="url-list-item__content">
      <div
        class="_row_tinyurl url-list-item__tinyurl"
        @click="selectTinyurl"
      >
        <p> {{ alias.domain }}/{{ alias.alias }} </p>

        <p
          v-if="alias.expires_at"
          class="url-list-item__expire-date"
        >
          {{ __('organization_dashboard.url_list_item.expires', { date: toRelativeTime(alias.expires_at) }) }}
        </p>
      </div>

      <p class="_row_url url-list-item__long-url text-clamper-2">
        {{ item.url }}
      </p>

      <p
        v-if="alias.description"
        class="url-list-item__description text-clamper-1"
      >
        {{ alias.description }}
      </p>

      <div
        v-if="alias.tags.length"
        class="url-list-item__tags"
      >
        <TuButton
          v-for="tag in alias.tags"
          :key="tag.name"
          :class="[`_tag _${tag.name}`]"
          :label="tag.name"
          :severity="ButtonSeverity.CANCEL"
          :size="ButtonSize.SMALL"
          @click.stop="onTagClick(tag)"
        />
      </div>

      <div class="_row_meta url-list-item__meta-wrapper">
        <div class="url-list-item__meta-info">
          <p class="url-list-item__created-date-time">
            {{ alias.createdAt().fromNow() }}
          </p>

          <span>|</span>

          <p class="url-list-item__stats">
            <template v-if="alias.stats.enabled">
              {{ alias.hits ?? 0 }}
              {{ __("click", {count: alias.hits ?? 0}) }}
            </template>
            <template v-else>
              {{ __('organization_dashboard.url_list_item.tracking_disabled') }}
            </template>
          </p>
        </div>

        <UserAvatar
          class="url-list-item__owner"
          :user="alias?.user"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.url-list-item {
  display: flex;
  gap: spacing.$xxxs;

  cursor: pointer;

  p {
    margin: 0px;
  }

  &__content {
    display: flex;
    gap: spacing.$xs-5;
    flex-flow: column;
    flex: 1;
  }

  &__tinyurl {
    display: flex;
    justify-content: space-between;

    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
    font-weight: typography.$font-weight-semi-bold;
  }

  &__expire-date {
    color: color.$text-secondary;
    font-size: typography.$xxs-font-size;
    line-height: typography.$xxs-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &__long-url {
    color: color.$secondary;
    font-size: typography.$xxs-font-size;
    line-height: typography.$xxs-line-height;
    font-weight: typography.$font-weight-semi-bold;
  }

  &__description {
    color: color.$text-secondary;
    font-size: typography.$xxs-font-size;
    line-height: typography.$xxs-line-height;
    font-weight: typography.$font-weight-semi-bold;
  }

  &__meta-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__meta-info {
    display: flex;
    align-items: center;
    gap: spacing.$xxxs;

    font-size: typography.$xs-font-size;
    line-height: typography.$xs-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &__tags {
    display: flex;
    gap: spacing.$xxxs;
  }

  &.active,
  &:hover {
    background-color: color.$background-secondary;
    border-right: 3px solid #28c98b;
  }
}
</style>
